/** @file Converts a bezier curve's handle values to the setup-relative units its scale key is stored in. */
import type { Curve, Doc } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';

/**
 * A scale key stores `absolute / setup` (relativeToSetup), and the compiler adds bezier `dv` to that stored value, so
 * the handles are divided by the setup scale too: the curve an agent asks for in channel units is the curve that plays
 * (final review m1). Other channels store `absolute - setup`, where `dv` is unchanged; ease curves are normalized.
 */
export function relativeCurve(doc: Doc, boneId: string, channel: string, curve: Curve): Curve {
  if (channel !== 'scaleX' && channel !== 'scaleY') return curve;
  if (typeof curve === 'string' || !('bezier' in curve)) return curve;
  const bone = doc.bones[boneId] ?? throwError('NOT_FOUND', { kind: 'bone', ref: boneId });
  const setup = bone.setup[channel];
  if (setup === 0) throwError('VALIDATION', undefined, { path: 'curve', hint: `setup ${channel} of bone ${bone.name} is 0, so bezier handles cannot be relative to it` });
  const [x1, dv1, x2, dv2] = curve.bezier;
  return { bezier: [x1, dv1 / setup, x2, dv2 / setup] };
}
